import { t } from "../../../../shared/i18n/index.js";

const STASH_KEY = "mobile-unsaved-note-edits";
const NODE_STASH_KEY = "mobile-unsaved-node-edits";
const STASH_MAX_BYTES = 64 * 1024;
const STASH_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const VOLATILE_STASH_TOAST_INTERVAL_MS = 30 * 1000;

const memoryNoteStash = new Map(); // `${docId}:${noteId}` -> payload
const memoryNodeStash = new Map();
const volatileStashToastAt = new Map(); // kind -> timestamp

let getCallbacks = () => null;

export function configureRecoveryStash(deps) {
  getCallbacks = deps.getCallbacks || getCallbacks;
}

function callbacks() {
  return getCallbacks() || {};
}

function canPersistRecoveryStash() {
  // Logged-in vault content is E2EE-sensitive. Keep emergency recovery text in
  // memory only for authenticated users so we don't create an extra plaintext
  // at-rest copy in localStorage. Logged-out local-only mode still persists the
  // stash because local device storage is the user's only durability layer.
  return !callbacks().getCurrentUser?.();
}

const PRIVACY_KEYS = { node: "mobile.stash.privacyNode", note: "mobile.stash.privacyNote" };

function showVolatileStashWarning(kind) {
  const now = Date.now();
  const last = volatileStashToastAt.get(kind) || 0;
  if (now - last < VOLATILE_STASH_TOAST_INTERVAL_MS) return;
  volatileStashToastAt.set(kind, now);
  callbacks().showToast?.(
    t(PRIVACY_KEYS[kind] || PRIVACY_KEYS.note),
    { type: "warning" },
  );
}

export function dropStashEntriesForDoc(docId) {
  if (!docId) return;
  // Memory mirror.
  for (const key of [...memoryNoteStash.keys()]) {
    if (key.startsWith(`${docId}:`)) memoryNoteStash.delete(key);
  }
  for (const key of [...memoryNodeStash.keys()]) {
    if (key.startsWith(`${docId}:`)) memoryNodeStash.delete(key);
  }
  // Persisted localStorage entries (logged-out durable stash).
  for (const k of [STASH_KEY, NODE_STASH_KEY]) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const stash = JSON.parse(raw);
      if (!Array.isArray(stash)) continue;
      const filtered = stash.filter((entry) => entry?.docId !== docId);
      if (filtered.length === stash.length) continue;
      if (filtered.length === 0) localStorage.removeItem(k);
      else localStorage.setItem(k, JSON.stringify(filtered));
    } catch (e) { /* ignore */ }
  }
}

export function stashUnsavedNodeEdit(docId, nodeId, payload) {
  memoryNodeStash.set(`${docId}:${nodeId}`, { ...payload, stashedAt: Date.now() });
  if (!canPersistRecoveryStash()) {
    showVolatileStashWarning("node");
    return false;
  }
  let persisted = false;
  try {
    const raw = localStorage.getItem(NODE_STASH_KEY);
    const stash = raw ? JSON.parse(raw) : [];
    const existingIdx = stash.findIndex((entry) => entry.docId === docId && entry.nodeId === nodeId);
    if (existingIdx >= 0) stash.splice(existingIdx, 1);
    stash.push({ docId, nodeId, payload, stashedAt: Date.now() });
    // Cap total payload size; FIFO-evict oldest entries. Stringifying inside
    // the loop is wasteful for the rare evict-many case; estimate size per
    // entry and drop in one pass, then stringify once at the end.
    let serialized = JSON.stringify(stash);
    if (serialized.length > STASH_MAX_BYTES && stash.length > 1) {
      const perEntry = Math.max(1, Math.ceil(serialized.length / stash.length));
      const targetLen = Math.max(1, Math.floor(STASH_MAX_BYTES / perEntry));
      stash.splice(0, Math.max(0, stash.length - targetLen));
      serialized = JSON.stringify(stash);
      // Belt-and-braces: if estimate undershot, drop one more at a time.
      while (serialized.length > STASH_MAX_BYTES && stash.length > 1) {
        stash.shift();
        serialized = JSON.stringify(stash);
      }
    }
    localStorage.setItem(NODE_STASH_KEY, serialized);
    persisted = true;
  } catch (e) {
    callbacks().showToast?.(
      t("mobile.stash.fullNode"),
      { type: "error" },
    );
  }
  return persisted;
}

export function peekStashedNodeEdit(docId, nodeId) {
  const key = `${docId}:${nodeId}`;
  if (canPersistRecoveryStash()) {
    try {
      const raw = localStorage.getItem(NODE_STASH_KEY);
      if (raw) {
        const stash = JSON.parse(raw);
        const entry = stash.findLast
          ? stash.findLast((e) => e.docId === docId && e.nodeId === nodeId)
          : [...stash].reverse().find((e) => e.docId === docId && e.nodeId === nodeId);
        if (entry?.payload) return entry.payload;
      }
    } catch (e) { /* fall through */ }
  }
  const mem = memoryNodeStash.get(key);
  return mem ? { name: mem.name, startDate: mem.startDate, endDate: mem.endDate, notes: mem.notes } : null;
}

export function takeStashedNodeEdit(docId, nodeId) {
  const key = `${docId}:${nodeId}`;
  let payload = null;
  if (canPersistRecoveryStash()) {
    try {
      const raw = localStorage.getItem(NODE_STASH_KEY);
      if (raw) {
        const stash = JSON.parse(raw);
        let idx = -1;
        for (let i = stash.length - 1; i >= 0; i--) {
          const entry = stash[i];
          if (entry.docId === docId && entry.nodeId === nodeId) {
            idx = i;
            break;
          }
        }
        if (idx < 0) idx = stash.findIndex(
          (entry) => entry.docId === docId && entry.nodeId === nodeId,
        );
        if (idx >= 0) {
          const [entry] = stash.splice(idx, 1);
          localStorage.setItem(NODE_STASH_KEY, JSON.stringify(stash));
          payload = entry.payload || null;
        }
      }
    } catch (e) { /* fall through */ }
  }
  if (!payload) {
    const mem = memoryNodeStash.get(key);
    if (mem) payload = { name: mem.name, startDate: mem.startDate, endDate: mem.endDate, notes: mem.notes };
  }
  memoryNodeStash.delete(key);
  return payload;
}

export function stashUnsavedNoteEdit(docId, noteId, payload) {
  // Always mirror to memory so recovery works in this session even if
  // persistence fails.
  memoryNoteStash.set(`${docId}:${noteId}`, { ...payload, stashedAt: Date.now() });
  if (!canPersistRecoveryStash()) {
    showVolatileStashWarning("note");
    return false;
  }
  let persisted = false;
  try {
    const raw = localStorage.getItem(STASH_KEY);
    const stash = raw ? JSON.parse(raw) : [];
    const existingIdx = stash.findIndex((entry) => entry.docId === docId && entry.noteId === noteId);
    if (existingIdx >= 0) stash.splice(existingIdx, 1);
    stash.push({ docId, noteId, payload, stashedAt: Date.now() });
    let serialized = JSON.stringify(stash);
    if (serialized.length > STASH_MAX_BYTES && stash.length > 1) {
      const perEntry = Math.max(1, Math.ceil(serialized.length / stash.length));
      const targetLen = Math.max(1, Math.floor(STASH_MAX_BYTES / perEntry));
      stash.splice(0, Math.max(0, stash.length - targetLen));
      serialized = JSON.stringify(stash);
      while (serialized.length > STASH_MAX_BYTES && stash.length > 1) {
        stash.shift();
        serialized = JSON.stringify(stash);
      }
    }
    localStorage.setItem(STASH_KEY, serialized);
    persisted = true;
  } catch (e) {
    // Quota exceeded / Safari private browsing; surface a more concerning
    // warning since edits won't survive a refresh.
    callbacks().showToast?.(
      t("mobile.stash.fullNote"),
      { type: "error" },
    );
  }
  return persisted;
}

export function peekStashedNoteEdit(docId, noteId) {
  const key = `${docId}:${noteId}`;
  if (canPersistRecoveryStash()) {
    try {
      const raw = localStorage.getItem(STASH_KEY);
      if (raw) {
        const stash = JSON.parse(raw);
        const entry = stash.findLast
          ? stash.findLast((e) => e.docId === docId && e.noteId === noteId)
          : [...stash].reverse().find((e) => e.docId === docId && e.noteId === noteId);
        if (entry?.payload) return entry.payload;
      }
    } catch (e) { /* fall through to memory */ }
  }
  const mem = memoryNoteStash.get(key);
  return mem ? { title: mem.title, content: mem.content, updated: mem.updated } : null;
}

export function takeStashedNoteEdit(docId, noteId) {
  const key = `${docId}:${noteId}`;
  let payload = null;
  if (canPersistRecoveryStash()) {
    try {
      const raw = localStorage.getItem(STASH_KEY);
      if (raw) {
        const stash = JSON.parse(raw);
        let idx = -1;
        for (let i = stash.length - 1; i >= 0; i--) {
          const entry = stash[i];
          if (entry.docId === docId && entry.noteId === noteId) {
            idx = i;
            break;
          }
        }
        if (idx < 0) idx = stash.findIndex(
          (entry) => entry.docId === docId && entry.noteId === noteId,
        );
        if (idx >= 0) {
          const [entry] = stash.splice(idx, 1);
          localStorage.setItem(STASH_KEY, JSON.stringify(stash));
          payload = entry.payload || null;
        }
      }
    } catch (e) { /* fall through */ }
  }
  if (!payload) {
    const mem = memoryNoteStash.get(key);
    if (mem) payload = { title: mem.title, content: mem.content, updated: mem.updated };
  }
  memoryNoteStash.delete(key);
  return payload;
}

export function pruneExpiredStashes() {
  for (const key of [STASH_KEY, NODE_STASH_KEY]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const stash = JSON.parse(raw);
      const cutoff = Date.now() - STASH_TTL_MS;
      const fresh = stash.filter((entry) => (entry?.stashedAt || 0) >= cutoff);
      if (fresh.length === stash.length) continue;
      if (fresh.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(fresh));
      }
    } catch (e) {
      // Corrupt JSON or quota; drop the key so a fresh stash can start.
      try { localStorage.removeItem(key); } catch (_) {}
    }
  }
}

export function clearMemoryStashes() {
  memoryNoteStash.clear();
  memoryNodeStash.clear();
  volatileStashToastAt.clear();
}
